import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { usePublicConfig } from "@/lib/useConfig";
import { useAuth } from "@/context/AuthContext";
import { PublicFooter, PublicNav } from "@/components/PublicNav";
import { Button } from "@/components/ui/button";

const LOOKUP = { pro: "pro_monthly", career_plus: "career_plus_monthly" };

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const { trialDays } = usePublicConfig();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/subscriptions/plans").then(({ data }) => setPlans(data.plans)).catch(() => setPlans([]));
  }, []);

  const choose = async (slug) => {
    if (!LOOKUP[slug]) {
      navigate(user ? "/dashboard" : "/register");
      return;
    }
    if (!user) {
      navigate("/register");
      return;
    }
    setBusy(slug);
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: LOOKUP[slug],
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(errMsg(e, "Unable to start checkout"));
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen">
      <PublicNav />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <p className="overline text-primary">pricing</p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-display font-black tracking-tighter leading-none">
          Config-driven plans.<br />No annual lock-in.
        </h1>
        <p className="mt-5 text-sm text-muted-foreground max-w-xl">
          Every new account starts with <span className="text-primary font-medium">{trialDays} days of full Career Plus access</span> —
          all modules, labs, capstone reviews and mock interviews. When the trial ends, pick a plan to keep going.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border" data-testid="pricing-grid">
          {plans.map((p) => (
            <div key={p.slug} data-testid={`plan-${p.slug}`} className={`bg-background p-8 flex flex-col ${p.highlighted ? "ring-1 ring-inset ring-primary" : ""}`}>
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-xl font-bold tracking-tight">{p.name}</h2>
                {p.highlighted && <span className="overline text-primary">recommended</span>}
              </div>
              <p className="mt-6 font-display text-4xl font-black tracking-tighter">
                ${p.priceMonthly}<span className="text-sm font-normal text-muted-foreground">/month</span>
              </p>
              <p className="mt-3 text-sm text-muted-foreground">{p.tagline}</p>
              <ul className="mt-7 space-y-3 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2.5">
                    <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 font-mono text-xs text-muted-foreground">
                AI requests/day: {p.limits?.aiRequestsPerDay}
              </p>
              <Button
                className="mt-5 w-full rounded-none gap-2"
                variant={p.highlighted ? "default" : "outline"}
                onClick={() => choose(p.slug)}
                disabled={busy === p.slug}
                data-testid={`plan-cta-${p.slug}`}
              >
                {busy === p.slug && <Loader2 className="h-4 w-4 animate-spin" />}
                {p.priceMonthly === 0 ? "Start free" : `Choose ${p.name}`}
              </Button>
            </div>
          ))}
        </div>
        {error && <p data-testid="pricing-error" className="mt-6 text-sm text-destructive">{error}</p>}
        <p className="mt-8 font-mono text-xs text-muted-foreground">
          Secure Stripe checkout. Test card 4242 4242 4242 4242, any future expiry and CVC. No card details touch this app.
        </p>
        <p className="mt-2 font-mono text-xs text-muted-foreground">
          Not signed in? <Link to="/register" className="text-primary hover:underline" data-testid="pricing-register-link">create a free account</Link> first.
        </p>
      </section>
      <PublicFooter />
    </div>
  );
}
